import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "./ui/button";
import { Copy, Download, ExternalLink, Check, BarChart3, Clock, KeyRound } from "lucide-react";
import { API_URL } from "../lib/api";
import { Link } from "react-router-dom";
import { toast } from "sonner";

export function ResultCard({ link }) {
    const [copied, setCopied] = useState(false);
    const [copiedInline, setCopiedInline] = useState(false);
    const [qrCopied, setQrCopied] = useState(false);
    if (!link) return null;

    const shortUrl = link.short_url;

    const copy = async () => {
        try {
            await navigator.clipboard.writeText(shortUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {}
    };
    const copyInline = async (e) => {
        e.preventDefault();
        e.stopPropagation();
        try {
            await navigator.clipboard.writeText(shortUrl);
            setCopiedInline(true);
            setTimeout(() => setCopiedInline(false), 1500);
        } catch {}
    };
    const copyQr = async () => {
        try {
            const res = await fetch(`${API_URL}/qr/${link.slug}?fmt=png`);
            const blob = await res.blob();
            if (typeof ClipboardItem !== "undefined" && navigator.clipboard?.write) {
                await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
                setQrCopied(true);
                toast.success("QR code copied to clipboard");
                setTimeout(() => setQrCopied(false), 1500);
            } else {
                // Fallback: copy image URL
                await navigator.clipboard.writeText(`${API_URL}/qr/${link.slug}?fmt=png`);
                setQrCopied(true);
                toast.success("QR image URL copied (image copy not supported here)");
                setTimeout(() => setQrCopied(false), 1500);
            }
        } catch (e) {
            toast.error("Could not copy QR code");
        }
    };

    const isPending = !!link.pending;
    const registerHref = isPending
        ? `/register?pending=${encodeURIComponent(link.pending_id)}`
        : null;
    const loginHref = isPending
        ? `/login?pending=${encodeURIComponent(link.pending_id)}`
        : null;

    return (
        <div className="mt-8" data-testid="result-card">
            {isPending && (
                <div className="mb-3 flex flex-col justify-between gap-3 rounded-t-2xl border-2 border-b-0 border-[hsl(27,96%,55%)]/50 bg-[hsl(27,96%,55%)]/10 p-4 sm:flex-row sm:items-center" data-testid="pending-banner">
                    <div className="flex items-start gap-3">
                        <Clock className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(27,96%,45%)]" />
                        <div>
                            <div className="font-display text-sm font-semibold text-foreground">
                                Preview — register within 30 min to activate
                            </div>
                            <div className="text-xs text-muted-foreground">
                                We've reserved <span className="font-mono">/{link.slug}</span> for you.
                                Create a free account (or sign in) and this short URL + QR code go live under your account, permanently.
                            </div>
                        </div>
                    </div>
                    <div className="flex shrink-0 gap-2">
                        <Button asChild size="sm" variant="outline" data-testid="pending-signin-btn">
                            <Link to={loginHref}>Sign in</Link>
                        </Button>
                        <Button asChild size="sm" className="bg-[hsl(27,96%,45%)] hover:bg-[hsl(27,96%,40%)] text-white gap-2" data-testid="pending-register-btn">
                            <Link to={registerHref}>
                                <KeyRound className="h-3.5 w-3.5" />
                                Activate
                            </Link>
                        </Button>
                    </div>
                </div>
            )}
            <div className={`grid grid-cols-1 gap-0 overflow-hidden border-2 border-border bg-card md:grid-cols-[1fr_auto] ${isPending ? "rounded-b-2xl border-t-0" : "rounded-2xl"}`}>
                <div className="flex flex-col justify-between p-6 sm:p-8">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Your short link</div>
                        <div className="mt-2 flex items-start gap-2">
                            <a
                                href={shortUrl}
                                target="_blank"
                                rel="noreferrer"
                                className="block flex-1 break-all font-mono text-2xl font-semibold text-primary hover:underline sm:text-3xl"
                                data-testid="result-short-url"
                            >
                                {shortUrl}
                            </a>
                            <button
                                type="button"
                                onClick={copyInline}
                                aria-label="Copy short link"
                                className="mt-1 inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-border bg-card text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                                data-testid="inline-copy-url-btn"
                            >
                                {copiedInline ? <Check className="h-4 w-4 text-[hsl(100,76%,35%)]" /> : <Copy className="h-4 w-4" />}
                            </button>
                        </div>
                        <div className="mt-4 truncate text-sm text-muted-foreground">
                            → <span className="font-mono">{link.long_url}</span>
                        </div>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-2">
                        <Button onClick={copy} className="gap-2" data-testid="copy-link-btn">
                            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                            {copied ? "Copied!" : "Copy link"}
                        </Button>
                        <Button variant="outline" asChild data-testid="open-link-btn">
                            <a href={shortUrl} target="_blank" rel="noreferrer" className="gap-2">
                                <ExternalLink className="h-4 w-4" /> Open
                            </a>
                        </Button>
                        <Button variant="outline" asChild data-testid="download-png-btn">
                            <a href={`${API_URL}/qr/${link.slug}?fmt=png`} target="_blank" rel="noreferrer" download className="gap-2">
                                <Download className="h-4 w-4" /> PNG
                            </a>
                        </Button>
                        <Button variant="outline" asChild data-testid="download-svg-btn">
                            <a href={`${API_URL}/qr/${link.slug}?fmt=svg`} target="_blank" rel="noreferrer" download className="gap-2">
                                <Download className="h-4 w-4" /> SVG
                            </a>
                        </Button>
                        {link.link_id && !isPending && (
                            <Button variant="outline" asChild data-testid="view-analytics-btn">
                                <Link to={`/link/${link.link_id}`} className="gap-2">
                                    <BarChart3 className="h-4 w-4" /> Analytics
                                </Link>
                            </Button>
                        )}
                    </div>
                </div>
                <div className="flex flex-col items-center justify-center gap-3 border-t border-border bg-background p-6 md:border-l md:border-t-0">
                    <div className="rounded-md bg-white p-3">
                        <QRCodeSVG
                            value={shortUrl}
                            size={168}
                            bgColor="#ffffff"
                            fgColor="#0F172A"
                            level="M"
                            includeMargin={false}
                            data-testid="qr-svg"
                        />
                    </div>
                    <button
                        type="button"
                        onClick={copyQr}
                        aria-label="Copy QR code image"
                        className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                        data-testid="inline-copy-qr-btn"
                    >
                        {qrCopied ? <Check className="h-3.5 w-3.5 text-[hsl(100,76%,35%)]" /> : <Copy className="h-3.5 w-3.5" />}
                        {qrCopied ? "Copied" : "Copy QR"}
                    </button>
                </div>
            </div>
        </div>
    );
}
